import type { IncomeStreamFlag } from "./insights";
import apiClient from "./client";
import { bucketLabel } from "@/lib/bucketLabels";
import { cadenceLabel, NEEDS_CATEGORY_LABEL_COPY } from "@fomofi/shared";
import {
  computeMonthlyIncome,
  type BillGroup,
  type CashFlowStabilitySnapshot,
  type Confidence,
  type CreditCardRow,
  type DebtUsage,
  type DiscretionaryTrend,
  type FinancialAnalysis,
  type FinancialInsights,
  type IncomeCrossCheck,
  type IncomeFrequency,
  type IncomeSource,
  type MonthlySpending,
  type SpendingSegment,
  type TopMerchant,
} from "@/data/mockFinancialAnalysis";

/* ─────────────────────── API response types (snake_case) ───────────────────────
 * Mirrors backend/apps/financial_analysis/serializers.py (FinancialAnalysisSerializer).
 * Monetary values are JSON numbers (DRF FloatField), not Decimal strings. */

export interface ApiIncomeSource {
  id: number;
  name: string;
  type: string | null; // PARTIAL — no income-type classifier yet (frontend default)
  amount_per_period: number;
  frequency: string;
  frequency_label: string;
  /** #1385: the two days of a twice-a-month source; `[]` otherwise. Optional for a
   *  backend that predates it. */
  semimonthly_days?: number[];
  last_received: string | null;
  confidence: Confidence;
  sparkline: number[];
  /** #715 per-source cross-check. Optional for backward compat with a backend
   *  that predates the fields. */
  expected_monthly?: number;
  observed_monthly?: number;
  match_status?: IncomeMatchStatus;
}

/** How a stream's rendered amount compares to its own backing transactions (#715).
 *  `unmatched` = no transaction carries this merchant name, so the figure is
 *  unevidenced — which is the state every Plaid stream with a null merchant_name
 *  lands in. */
export type IncomeMatchStatus = "ok" | "diverges" | "unmatched" | "unverifiable";

/** §1 headline vs transaction-derived income over the same basis months (#715). */
export interface ApiIncomeCrossCheck {
  detected_monthly: number;
  observed_monthly: number;
  basis_months: number;
  divergence_pct: number | null;
  status: "ok" | "under_detected" | "over_detected" | "no_streams" | "unverifiable";
  caveat: string | null;
}

export interface ApiBill {
  id: number;
  merchant: string;
  amount: number; // already monthly-normalized server-side
  frequency: string;
  last_seen: string | null;
}

export interface ApiBillGroup {
  id: "needs" | "wants";
  monthly_total: number;
  bills: ApiBill[];
}

export interface ApiCreditCard {
  id: number;
  account: string;
  last_four: string;
  balance: number;
  detection: string;
  detection_confidence: Confidence;
  usage: DebtUsage;
  /** True when `usage` is the USER's stored choice; false when it is the backend's
   *  heuristic fallback. The two used to be indistinguishable, which is how FA §8
   *  came to show "Debt & credit card usage confirmed" with a green tick while
   *  `debt_usage` was null on 139 of 139 liability accounts (BEN-141 4b item 4).
   *  Optional for backward compat with a pre-4b backend. */
  usage_confirmed?: boolean;
  monthly_payment: number | null; // BLOCKED #381
}

/** A correctable merchant inside a segment (BEN-141 item 1a). */
export interface ApiTopMerchant {
  name: string;
  /** MONTHLY average for this merchant, on the same divisor as the section. */
  amount: number;
  /** A real, most-recent row for this merchant — the id the categorise POST uses. */
  sample_transaction_id: number;
}

/** Contract batch 13 (SC23): one category's monthly average inside a segment. */
export interface ApiSpendingCategory {
  /** Null for a row given a parent but no category, and for ledger-only entries. */
  category_id: number | null;
  name: string;
  /** MONTHLY, on the same divisor and basis months as the segment. */
  amount: number;
  transaction_count: number;
  /** How many of the basis months had a row in this category. */
  months_active: number;
}

export interface ApiSpendingSegment {
  /** FP-176 extension: `uncategorized` is spending that still needs a category, sent
   *  only when there is some. It is inside `monthly_average`. */
  key: "needs" | "wants" | "savings" | "debt_payments" | "uncategorized";
  amount: number;
  percent: number;
  transaction_count: number;
  top_merchants: ApiTopMerchant[];
  /** Optional: a cached pre-batch-13 response has none (fa:v7 makes that short). */
  categories?: ApiSpendingCategory[];
}

/* The BASIS fields (BEN-141). Every monthly average on this payload rests on a
 * count of COMPLETE months, and that count is small or zero far more often than
 * the seeded fixtures suggested. `months_used === 0` means the divisor fell back
 * to a single partial month: the figure exists but must be caveated, never shown
 * bare. These are declared in FinancialAnalysisSerializer as of batch 2 — before
 * that they were on the wire but undeclared, so consuming them was invisible to
 * type-checking. See MONTHS_USED_FLOOR in @/lib/dataBasis. */

export interface ApiSpending {
  monthly_average: number;
  segments: ApiSpendingSegment[];
  months_used: number;
  /* Categorisation coverage (BEN-141). OPTIONAL, though the backend always emits
   * them: `get_financial_analysis` caches per user for 3600s, so for up to an hour
   * after the release that added these, a warm cache entry answers without them.
   * `adaptSpending` defaults accordingly — undefined must read as "nothing to
   * flag", never as 0% coverage on an account with no problem. */
  /** Expense rows §4 could not place, in the same basis months as the average. */
  uncategorized_count?: number;
  /** Those rows as a MONTHLY figure — the amount monthly_average falls short by. */
  uncategorized_amount?: number;
  /** CAT-017: outflows the user filed as Transfer or Income, left out of spend on
   *  purpose. Categorised, so neither uncategorised nor part of coverage. */
  excluded_count?: number;
  /** Those rows as a MONTHLY figure, on the same divisor. */
  excluded_amount?: number;
  /** Share of basis-month outflow DOLLARS that landed in a segment; 100 = nothing
   *  to place. §4 suppresses its coverage line entirely at 100. */
  coverage_pct?: number;
  /** Contract batch 24 (DE20): payments to the user's own linked cards, monthly. A
   *  transfer, in no segment; the cards' purchases are the spending. */
  card_payments_routed?: number;
  /** Their net paydown (payments above the purchases), monthly, already inside the
   *  debt_payments segment. */
  card_net_paydown?: number;
}

export interface ApiTrendPoint {
  month: string;
  amount: number;
  top_category: string | null;
  complete?: boolean;
  in_basis?: boolean;
}

export interface ApiDiscretionary {
  monthly_average: number;
  /** null when the basis is too thin to call anything a trend — the backend
   *  floors it at MIN_COMPARISON_MONTHS months per half. NEVER coalesce this to
   *  0: a zero renders as "flat", which is a claim the data cannot support. */
  delta_pct: number | null;
  months_used: number;
  /** Months in EACH half of the delta_pct comparison. Kept even when delta_pct
   *  is suppressed, so a surface can say how thin the history is. */
  comparison_months: number;
  series: ApiTrendPoint[];
  /** Contract batch 14 (SC26). Optional: a cached pre-v8 response has none. */
  pending_review?: {
    is_provisional: boolean;
    count: number;
    debits_monthly: number;
    credits_monthly: number;
  };
}

export interface ApiCashFlowPoint {
  month: string;
  income: number;
  expenses: number;
  net: number;
  /** A whole month since the user's first transaction. Drives the caveat. */
  complete: boolean;
  /** Did this month actually feed the averages? Equals `complete` except when
   *  every complete month was empty and the basis fell back. */
  in_basis: boolean;
}

export interface ApiCashFlow {
  average_net: number;
  volatility_score: number | null;
  rating: CashFlowStabilitySnapshot["rating"];
  /** The shared caveat signal; insights.months_analyzed mirrors this field. */
  months_used: number;
  series: ApiCashFlowPoint[];
}

export interface ApiInsight {
  id: string;
  text: string;
}

export interface ApiInsights {
  strengths: ApiInsight[];
  risks: ApiInsight[];
  opportunities: ApiInsight[];
}

export interface ApiFinancialAnalysis {
  analyzed_range_label: string;
  /** Calendar months SCANNED — the span `analyzed_range_label` names. NOT the
   *  divisor behind any average on this page; that is `spending.months_used`,
   *  which counts complete months only. Optional for backward compat with a
   *  backend that predates the field (BEN-141 B2). */
  analyzed_window_months?: number;
  analyzed_completed_date: string;
  income_sources: ApiIncomeSource[];
  /** §1's headline, totalled server-side (#715). Optional for backward compat
   *  with a backend that predates the field. */
  income_sources_monthly_total?: number;
  /** Optional for backward compat with a backend that predates #715. */
  income_cross_check?: ApiIncomeCrossCheck;
  /** RD-11: every income deposit per month, one-off money included ("Average
   *  deposits, last 12 months"). Not monthly income. */
  deposits_monthly_average?: number;
  /** Income (b): paychecks left out of the §1 total, and kept ones. */
  income_streams_excluded?: IncomeStreamFlag[];
  income_streams_kept?: IncomeStreamFlag[];
  total_recurring_payments_count: number;
  bill_groups: ApiBillGroup[];
  credit_cards: ApiCreditCard[];
  spending: ApiSpending;
  discretionary: ApiDiscretionary;
  cash_flow: ApiCashFlow;
  insights: ApiInsights;
}

export async function getFinancialAnalysis(): Promise<ApiFinancialAnalysis> {
  const { data } = await apiClient.get<ApiFinancialAnalysis>("/financial-analysis/");
  return data;
}

/* ───────────────────────────────── adapter ─────────────────────────────────
 * Maps the lean API response onto the UI's camelCase shape, supplying the
 * presentation fields the backend intentionally omits (labels, colors, layout
 * flags) and falling back only for documented BLOCKED fields. */

// #1385: "semimonthly" was missing, so a detected twice-a-month paycheck fell
// through to "monthly" here and was shown, edited and re-saved at half its income.
const VALID_FREQUENCIES: IncomeFrequency[] = [
  "weekly",
  "biweekly",
  "semimonthly",
  "monthly",
  "quarterly",
  "yearly",
];

const BILL_GROUP_LABELS: Record<ApiBillGroup["id"], string> = {
  needs: bucketLabel("needs"),
  wants: bucketLabel("wants"),
};
// Presentation: needs render as a flat list, wants as logo circles (mock convention).
const BILL_GROUP_SHOW_LOGOS: Record<ApiBillGroup["id"], boolean> = {
  needs: false,
  wants: true,
};

const SEGMENT_LABELS: Record<ApiSpendingSegment["key"], string> = {
  needs: bucketLabel("needs"),
  wants: bucketLabel("wants"),
  savings: bucketLabel("savings"),
  // BEN-141: FA's segment is MEASURED debt outflow, not the per-period plan.
  debt_payments: bucketLabel("debt-paid"),
  uncategorized: NEEDS_CATEGORY_LABEL_COPY,
};
const SEGMENT_COLORS: Record<ApiSpendingSegment["key"], string> = {
  needs: "var(--needs)",
  wants: "var(--wants)",
  savings: "var(--savings)",
  debt_payments: "var(--debt-allocation)",
  // A state, not a bucket: neutral, never a bucket colour.
  uncategorized: "var(--muted-foreground)",
};

function toFrequency(value: string): IncomeFrequency {
  return (VALID_FREQUENCIES as string[]).includes(value) ? (value as IncomeFrequency) : "monthly";
}

function adaptIncomeCrossCheck(xc: ApiIncomeCrossCheck | undefined): IncomeCrossCheck | null {
  if (!xc) return null;
  return {
    detectedMonthly: xc.detected_monthly,
    observedMonthly: xc.observed_monthly,
    basisMonths: xc.basis_months,
    divergencePct: xc.divergence_pct,
    status: xc.status,
    caveat: xc.caveat,
  };
}

function adaptIncomeSource(s: ApiIncomeSource): IncomeSource {
  const frequency = toFrequency(s.frequency);
  return {
    id: String(s.id),
    name: s.name,
    // type is PARTIAL (null from API); not surfaced in the new Section 1 design.
    type: "Other",
    amountPerPeriod: s.amount_per_period,
    frequency,
    semimonthlyDays: frequency === "semimonthly" ? (s.semimonthly_days ?? []) : undefined,
    frequencyLabel: s.frequency_label || cadenceLabel(frequency),
    lastReceived: s.last_received ?? "—",
    confidence: s.confidence,
    sparkline: s.sparkline,
    expectedMonthly: s.expected_monthly ?? null,
    observedMonthly: s.observed_monthly ?? null,
    matchStatus: s.match_status ?? null,
  };
}

/**
 * PUB-F47 / PLN-F62 (Paul, 2026-10-07: "cap it, don't delete"): §2's subscription logo
 * grid shows this many, then a "+N more" tile that opens the rest. The tile existed but
 * was dead on live data: this adapter never set `visibleCount`, so every logo showed
 * and the control could not appear. 4 is the cap the grid was designed with
 * (`mockFinancialAnalysis.ts`).
 */
export const SUBSCRIPTION_LOGO_CAP = 4;

function adaptBillGroup(g: ApiBillGroup): BillGroup {
  return {
    id: g.id,
    label: BILL_GROUP_LABELS[g.id],
    monthlyTotal: g.monthly_total,
    bills: g.bills.map((b) => ({
      id: String(b.id),
      merchant: b.merchant,
      amount: b.amount,
      frequency: b.frequency,
      lastSeen: b.last_seen ?? undefined,
    })),
    showLogoCircles: BILL_GROUP_SHOW_LOGOS[g.id],
    ...(BILL_GROUP_SHOW_LOGOS[g.id] ? { visibleCount: SUBSCRIPTION_LOGO_CAP } : {}),
    defaultOpen: true,
  };
}

function adaptCreditCard(c: ApiCreditCard): CreditCardRow {
  return {
    id: String(c.id),
    account: c.account,
    lastFour: c.last_four,
    balance: c.balance,
    detection: c.detection,
    detectionConfidence: c.detection_confidence,
    usage: c.usage,
    usageConfirmed: c.usage_confirmed ?? false,
    // monthly_payment is BLOCKED (#381) — null from API, hidden in the UI.
    monthlyPayment: c.monthly_payment ?? undefined,
  };
}

function adaptTopMerchant(m: ApiTopMerchant): TopMerchant {
  return { name: m.name, amount: m.amount, sampleTransactionId: m.sample_transaction_id };
}

/** Exported so the object payload is asserted against the real adapter. */
export function adaptSpending(s: ApiSpending): MonthlySpending {
  const segments: SpendingSegment[] = s.segments.map((seg) => ({
    key: seg.key,
    label: SEGMENT_LABELS[seg.key],
    amount: seg.amount,
    percent: seg.percent,
    color: SEGMENT_COLORS[seg.key],
    transactionCount: seg.transaction_count,
    topMerchants: seg.top_merchants.map(adaptTopMerchant),
    categories: (seg.categories ?? []).map((c) => ({
      categoryId: c.category_id,
      name: c.name,
      amount: c.amount,
      transactionCount: c.transaction_count,
      monthsActive: c.months_active,
    })),
  }));
  return {
    monthlyAverage: s.monthly_average,
    segments,
    monthsUsed: s.months_used,
    // `?? 100` / `?? 0`, not a bare read: a cached FA response written before
    // these fields existed has them undefined, and an undefined coverage must
    // read as "nothing to flag" rather than rendering "0% of your spending" on
    // an account with no problem.
    uncategorizedCount: s.uncategorized_count ?? 0,
    uncategorizedAmount: s.uncategorized_amount ?? 0,
    excludedCount: s.excluded_count ?? 0,
    excludedAmount: s.excluded_amount ?? 0,
    coveragePct: s.coverage_pct ?? 100,
    cardPaymentsRouted: s.card_payments_routed ?? 0,
  };
}

function adaptDiscretionary(d: ApiDiscretionary): DiscretionaryTrend {
  return {
    monthlyAverage: d.monthly_average,
    // NOT `?? 0`. A null delta means "the basis is too thin to call this a
    // trend"; zero means "spending was flat". Collapsing the first into the
    // second is what let §5 render an authoritative arrow and colour off a
    // comparison the backend had already declined to make (BEN-141 1e).
    deltaPct: d.delta_pct,
    monthsUsed: d.months_used,
    comparisonMonths: d.comparison_months,
    series: d.series.map((p) => ({
      month: p.month,
      amount: p.amount,
      topCategory: p.top_category ?? undefined,
      complete: p.complete,
      inBasis: p.in_basis,
    })),
    pendingReview: {
      isProvisional: d.pending_review?.is_provisional ?? false,
      count: d.pending_review?.count ?? 0,
    },
  };
}

function adaptCashFlow(c: ApiCashFlow): CashFlowStabilitySnapshot {
  return {
    averageNet: c.average_net,
    volatilityScore: c.volatility_score,
    rating: c.rating,
    monthsUsed: c.months_used,
    series: c.series.map((p) => ({
      month: p.month,
      income: p.income,
      expenses: p.expenses,
      net: p.net,
      complete: p.complete,
      inBasis: p.in_basis,
    })),
  };
}

function adaptInsights(i: ApiInsights): FinancialInsights {
  return { strengths: i.strengths, risks: i.risks, opportunities: i.opportunities };
}

export function toFinancialAnalysis(api: ApiFinancialAnalysis): FinancialAnalysis {
  return {
    analyzedRangeLabel: api.analyzed_range_label,
    analyzedWindowMonths: api.analyzed_window_months ?? null,
    analyzedCompletedDate: api.analyzed_completed_date,
    incomeSources: api.income_sources.map(adaptIncomeSource),
    monthlyIncomeTotal: api.income_sources_monthly_total ?? null,
    depositsMonthlyAverage: api.deposits_monthly_average ?? null,
    incomeStreamsExcluded: api.income_streams_excluded ?? [],
    incomeStreamsKept: api.income_streams_kept ?? [],
    incomeCrossCheck: adaptIncomeCrossCheck(api.income_cross_check),
    totalRecurringPaymentsCount: api.total_recurring_payments_count,
    billGroups: api.bill_groups.map(adaptBillGroup),
    creditCards: api.credit_cards.map(adaptCreditCard),
    spending: adaptSpending(api.spending),
    discretionary: adaptDiscretionary(api.discretionary),
    cashFlow: adaptCashFlow(api.cash_flow),
    insights: adaptInsights(api.insights),
  };
}

/**
 * §1's headline: total average monthly income.
 *
 * The server totals it (`income_sources_monthly_total`), and that is the figure
 * to show — it is the same number as `expected_monthly_income`, the 50/30/20
 * denominator, so the plan and the figure a user is asked to check cannot
 * diverge. #715 found the rule implemented four times, once here.
 *
 * The local reduce survives as the fallback for a backend that predates the
 * field, and as the live total while unsaved rows are being edited. Both are
 * cases where no server answer exists yet.
 */
export function totalMonthlyIncome(
  analysis: Pick<FinancialAnalysis, "monthlyIncomeTotal" | "incomeSources">,
): number {
  return analysis.monthlyIncomeTotal ?? computeMonthlyIncome(analysis.incomeSources);
}

/**
 * FA-008: the contract exposes only a confidence BAND (high >= 0.90, medium >=
 * 0.70, else low); the score itself stays server-side. The client used to map
 * the band to 95/80/60 and print an amount-weighted "{x}% confident", a precise
 * figure no measurement produced (a 0.71 score read "80% confident"). The band
 * is what is known, so the band is what is said.
 */
const CONFIDENCE_LABEL: Record<Confidence, string> = {
  high: "High confidence",
  medium: "Medium confidence",
  low: "Low confidence",
};

export function confidenceLabel(level: Confidence): string {
  return CONFIDENCE_LABEL[level];
}

const BAND_RANK: Record<Confidence, number> = { low: 1, medium: 2, high: 3 };
const RANK_BAND: Confidence[] = ["low", "low", "medium", "high"];

/** The band that carries most of the income, by amount (ranks, not invented
 *  percentages, are averaged), or null with no income sources. */
export function weightedConfidenceBand(sources: IncomeSource[]): Confidence | null {
  const totalWeight = sources.reduce((sum, s) => sum + Math.abs(s.amountPerPeriod), 0);
  if (totalWeight === 0) return null;
  const weighted = sources.reduce(
    (sum, s) => sum + BAND_RANK[s.confidence] * Math.abs(s.amountPerPeriod),
    0,
  );
  return RANK_BAND[Math.round(weighted / totalWeight)];
}
